import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import {
  AdminButtonComponent,
  AdminCardComponent,
  AdminConfirmDialogService,
  AdminEmptyStateComponent,
  AdminModalComponent,
  AdminPageHeaderComponent,
  AdminSkeletonComponent,
  AdminToastService,
} from '../../../../shared/admin';
import {
  emptyReleaseNoteForm,
  releaseMatchesSearch,
  releaseNoteToForm,
  type ReleaseNoteDto,
  type ReleaseNoteForm,
} from '../../../../shared/models/release-notes.models';
import { ReleaseNotesService } from '../../../../shared/services/release-notes.service';
import { AuthService } from '../../../../services/auth.service';
import { PermissionService } from '../../../permissions/permission.service';
import { ReleaseAccordionComponent } from './components/release-accordion/release-accordion.component';
import { ReleaseFormComponent } from './components/release-form/release-form.component';
import { ReleaseManagementTableComponent } from './components/release-management-table/release-management-table.component';
import { ReleaseSearchComponent } from './components/release-search/release-search.component';

@Component({
  selector: 'app-release-notes-page',
  standalone: true,
  imports: [
    RouterLink,
    AdminPageHeaderComponent,
    AdminCardComponent,
    AdminButtonComponent,
    AdminModalComponent,
    AdminEmptyStateComponent,
    AdminSkeletonComponent,
    ReleaseSearchComponent,
    ReleaseAccordionComponent,
    ReleaseFormComponent,
    ReleaseManagementTableComponent,
  ],
  templateUrl: './release-notes-page.component.html',
  styleUrl: './release-notes-page.component.scss',
})
export class ReleaseNotesPageComponent implements OnInit, OnDestroy {
  private readonly service = inject(ReleaseNotesService);
  private readonly auth = inject(AuthService);
  private readonly permissions = inject(PermissionService);
  private readonly toast = inject(AdminToastService);
  private readonly confirmDialog = inject(AdminConfirmDialogService);

  private searchTimer: ReturnType<typeof setTimeout> | undefined;

  readonly showCreate = computed(() => this.permissions.canShow('settings.releaseNotes', 'create'));
  readonly canCreate = computed(() => this.permissions.canExecute('settings.releaseNotes', 'create'));
  readonly showEdit = computed(() => this.permissions.canShow('settings.releaseNotes', 'edit'));
  readonly canEdit = computed(() => this.permissions.canExecute('settings.releaseNotes', 'edit'));
  readonly showPublish = computed(() => this.permissions.canShow('settings.releaseNotes', 'publish'));
  readonly canPublish = computed(() => this.permissions.canExecute('settings.releaseNotes', 'publish'));
  readonly showManage = computed(
    () => this.showCreate() || this.showEdit() || this.showPublish(),
  );
  readonly canManage = computed(
    () => this.canCreate() || this.canEdit() || this.canPublish(),
  );

  readonly adminReleases = signal<ReleaseNoteDto[]>([]);
  readonly publishedReleases = signal<ReleaseNoteDto[]>([]);
  readonly adminLoading = signal(true);
  readonly viewerLoading = signal(true);
  readonly adminError = signal<string | null>(null);
  readonly viewerError = signal<string | null>(null);

  readonly adminSearch = signal('');
  readonly viewerSearch = signal('');

  readonly showForm = signal(false);
  readonly editingId = signal<string | null>(null);
  readonly form = signal<ReleaseNoteForm>(emptyReleaseNoteForm());
  readonly saving = signal(false);
  readonly formError = signal<string | null>(null);

  readonly expandedViewerIds = signal<Set<string>>(new Set());
  readonly viewReleaseId = signal<string | null>(null);

  readonly filteredAdminReleases = computed(() => {
    const term = this.adminSearch();
    return this.adminReleases().filter((r) => releaseMatchesSearch(r, term));
  });

  readonly filteredPublishedReleases = computed(() => {
    const term = this.viewerSearch();
    return this.publishedReleases().filter((r) => releaseMatchesSearch(r, term));
  });

  readonly viewRelease = computed(() => {
    const id = this.viewReleaseId();
    if (!id) return null;
    return this.publishedReleases().find((r) => r._id === id) ?? this.adminReleases().find((r) => r._id === id) ?? null;
  });

  ngOnInit(): void {
    this.loadViewer();
    if (this.showManage()) {
      this.loadAdmin();
    } else {
      this.adminLoading.set(false);
    }
  }

  ngOnDestroy(): void {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  }

  onAdminSearchChange(term: string): void {
    this.adminSearch.set(term);
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.loadAdmin(), 300);
  }

  onViewerSearchChange(term: string): void {
    this.viewerSearch.set(term);
    const releases = this.publishedReleases().filter((r) => releaseMatchesSearch(r, term));
    if (term.trim()) {
      this.expandedViewerIds.set(new Set(releases.map((r) => r._id)));
    } else {
      this.setDefaultExpanded(releases);
    }
  }

  loadAdmin(): void {
    this.adminLoading.set(true);
    this.adminError.set(null);
    this.service.listPlatform({ search: this.adminSearch() }).subscribe({
      next: (items) => {
        this.adminReleases.set(items);
        this.adminLoading.set(false);
      },
      error: () => {
        this.adminError.set('Unable to load release notes. Please try again.');
        this.adminLoading.set(false);
      },
    });
  }

  loadViewer(): void {
    this.viewerLoading.set(true);
    this.viewerError.set(null);
    this.service.listPublic().subscribe({
      next: (items) => {
        this.publishedReleases.set(items);
        this.setDefaultExpanded(items);
        this.viewerLoading.set(false);
      },
      error: () => {
        this.viewerError.set('Unable to load published release notes. Please try again.');
        this.viewerLoading.set(false);
      },
    });
  }

  openCreate(): void {
    this.editingId.set(null);
    this.form.set(emptyReleaseNoteForm());
    this.formError.set(null);
    this.showForm.set(true);
  }

  openEdit(release: ReleaseNoteDto): void {
    this.editingId.set(release._id);
    this.form.set(releaseNoteToForm(release));
    this.formError.set(null);
    this.showForm.set(true);
  }

  closeForm(): void {
    this.showForm.set(false);
    this.editingId.set(null);
    this.formError.set(null);
  }

  saveForm(): void {
    const payload = this.form();
    if (!payload.versionNumber.trim() || !payload.buildNumber.trim() || !payload.title.trim()) {
      this.formError.set('Version, build number, and title are required.');
      return;
    }

    this.saving.set(true);
    this.formError.set(null);
    const body = {
      versionNumber: payload.versionNumber.trim(),
      buildNumber: payload.buildNumber.trim(),
      releaseDate: payload.releaseDate,
      title: payload.title.trim(),
      shortSummary: payload.shortSummary.trim(),
      status: payload.status,
      displayOrder: payload.displayOrder,
      updateItems: payload.updateItems,
    };

    const request$ = this.editingId()
      ? this.service.update(this.editingId()!, body)
      : this.service.create(body);

    request$.subscribe({
      next: () => {
        this.saving.set(false);
        this.showForm.set(false);
        this.toast.success(this.editingId() ? 'Release note updated' : 'Release note created');
        this.loadAdmin();
        this.loadViewer();
      },
      error: (err) => {
        this.saving.set(false);
        this.formError.set(err?.error?.message ?? 'Unable to save release note.');
      },
    });
  }

  async removeRelease(release: ReleaseNoteDto): Promise<void> {
    const confirmed = await this.confirmDialog.confirm({
      title: 'Delete release note',
      message: `Delete version ${release.versionNumber} (build ${release.buildNumber})? This cannot be undone.`,
      confirmLabel: 'Delete',
      confirmVariant: 'danger',
    });
    if (!confirmed) return;

    this.service.remove(release._id).subscribe({
      next: () => {
        this.toast.success('Release note deleted');
        this.loadAdmin();
        this.loadViewer();
      },
      error: () => this.toast.error('Unable to delete release note.'),
    });
  }

  duplicateRelease(release: ReleaseNoteDto): void {
    this.service.duplicate(release._id).subscribe({
      next: () => {
        this.toast.success('Release note duplicated');
        this.loadAdmin();
      },
      error: () => this.toast.error('Unable to duplicate release note.'),
    });
  }

  publishRelease(release: ReleaseNoteDto): void {
    this.service.publish(release._id).subscribe({
      next: () => {
        this.toast.success('Release note published');
        this.loadAdmin();
        this.loadViewer();
      },
      error: () => this.toast.error('Unable to publish release note.'),
    });
  }

  unpublishRelease(release: ReleaseNoteDto): void {
    this.service.unpublish(release._id).subscribe({
      next: () => {
        this.toast.success('Release note unpublished');
        this.loadAdmin();
        this.loadViewer();
      },
      error: () => this.toast.error('Unable to unpublish release note.'),
    });
  }

  viewReleaseInViewer(release: ReleaseNoteDto): void {
    this.viewReleaseId.set(release._id);
    this.expandedViewerIds.set(new Set([release._id]));
    document.getElementById('release-notes-viewer')?.scrollIntoView({ behavior: 'smooth' });
  }

  onExpandedChange(ids: Set<string>): void {
    this.expandedViewerIds.set(ids);
  }

  private setDefaultExpanded(releases: ReleaseNoteDto[]): void {
    if (releases.length === 0) {
      this.expandedViewerIds.set(new Set());
      return;
    }
    const sorted = [...releases].sort(
      (a, b) => new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime(),
    );
    this.expandedViewerIds.set(new Set([sorted[0]._id]));
  }
}
